import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import CommonAccordion from "../../custom-components/CommonAccordion";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import { ReactComponent as AutomateIcon } from "../../../images/automateIcon.svg";
import { ReactComponent as PenIcon } from "../../../images/penIcon.svg";
import { useNavigate } from "react-router-dom";
import CommonDownload from "../../custom-components/CommonDownload";
import FullscreenToggle from "../../custom-components/FullScreenToggle";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ReactComponent as ExportIcon } from "../../../images/exportIcon.svg";
import FormatDataForPDF from "../../../utils/FormatDataForPDF";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CommonCopy from "../../custom-components/CommonCopy";

const BugReportEnhancerPage = ({ bugReportData, enhancedBugReport }) => {
  const [bugReport, setBugReportData] = useState(bugReportData || {});
  const [enhancedReport, setEnhancedReport] = useState(enhancedBugReport || {});

  const [activeThumb, setActiveThumb] = useState(null);
  const [isCopied, setIsCopied] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (!bugReport || Object.keys(bugReport).length === 0) {
      return navigate("/dashboard/bugreportenhancer/input");
    }
  }, [bugReport]);

  const [openDownload, setOpenDownload] = useState(false);

  const handleDownload = () => {
    // console.log("Download clicked...");
  };

  const handleThumbClick = (thumbType) => {
    // Toggle thumbs based on the thumbType (like or dislike)
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };

const handleCopyClickData = () => {
  const labelOverrides = {
    Evidences_TBD: "Evidence",
  };

  const contentToCopy = Object.entries(fields)
    .map(([key, value]) => {
      const formattedKey = labelOverrides[key]
        ? labelOverrides[key]
        : key
            .replace(/_/g, " ")
            .split(" ")
            .map((word, index) => {
              if (
                index === 0 ||
                !["for", "and", "of", "in", "on", "at", "to", "by"].includes(
                  word.toLowerCase()
                )
              ) {
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
              }
              return word.toLowerCase();
            })
            .join(" ");

      let formattedValue;
      if (typeof value === "object" && !Array.isArray(value)) {
        formattedValue = Object.entries(value)
          .map(([subKey, subVal]) => {
            if (
              subKey.toLowerCase() === "test_steps" &&
              Array.isArray(subVal)
            ) {
              return `${subKey.replace(/_/g, " ")}:\n${subVal.join("\n")}`;
            } else if (typeof subVal === "object" && !Array.isArray(subVal)) {
              return `${subKey.replace(/_/g, " ")}:\n${Object.entries(subVal)
                .map(([k, v]) => `  - ${k}: ${v}`)
                .join("\n")}`;
            }
            return `${subKey.replace(/_/g, " ")}: ${subVal}`;
          })
          .join("\n");
      } else if (Array.isArray(value)) {
        formattedValue = value.join("\n");
      } else {
        formattedValue = value;
      }

      return `${formattedKey}:\n${formattedValue}`;
    })
    .join("\n\n");

  return contentToCopy; // ✅ You must return the result
};


  const [editMode, setEditMode] = useState({
    bugInput: false,
    title: false,
    description: false,
    stepsToReproduce: false,
    expectedVsActual: false,
  });

  const [fields, setFields] = useState(enhancedReport);
  const [tempFields, setTempFields] = useState({ ...fields });

  const handleEditClick = (field) => {
    setEditMode((prev) => ({ ...prev, [field]: true }));
    setTempFields((prev) => ({ ...prev, [field]: fields[field] }));
  };

  const handleFieldChange = (field, value) => {
    setTempFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveClick = (field) => {
    const updatedFields = { ...fields, [field]: tempFields[field] };
    setFields(updatedFields);
    setEditMode((prev) => ({ ...prev, [field]: false }));
    // saveToLocalStorage(updatedFields); // Save the changes to localStorage
  };

  const handleCancelClick = (field) => {
    setTempFields((prev) => ({ ...prev, [field]: fields[field] }));
    setEditMode((prev) => ({ ...prev, [field]: false }));
  };

  const renderFieldsFromResponse = (fields) => {
    return Object.keys(fields).map((fieldKey) => {
      // Helper function to format the title
      const formatTitle = (title) => {
        const exceptions = ["for", "and", "of", "in", "on", "at", "to", "by"];
        return title
          .replace(/_/g, " ") // Replace underscores with spaces
          .replace(/Evidences TBD/i, "Evidence") // Replace "Evidences TBD" with "Evidence"
          .split(" ")
          .map((word, index) => {
            if (index === 0 || !exceptions.includes(word.toLowerCase())) {
              return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            }
            return word.toLowerCase();
          })
          .join(" ");
      };

      const transformedTitle = formatTitle(fieldKey);

      return (
        <Grid item xs={12} key={fieldKey}>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              "&:hover .edit-icon": { display: "flex" },
              width: "100%",
            }}
          >
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{
                width: "300px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: "bold",
                color: "#000",
                fontSize: "14px",
              }}
            >
              {transformedTitle}
            </Typography>
            <Tooltip title="Click to edit" placement="right" arrow>
              <IconButton
                size="small"
                className="edit-icon"
                sx={{
                  alignSelf: "flex-start",
                }}
                onClick={() => handleEditClick(fieldKey)}
              >
                <PenIcon style={{ width: 18, height: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
          {renderEditableField(fieldKey, Array.isArray(fields[fieldKey]))}
        </Grid>
      );
    });
  };

  const renderEditableField = (field, multiline = false) => {
    const fieldValue = editMode[field] ? tempFields[field] : fields[field];

    // Function to format ideal test cases and other fields
    const formatIdealTestCases = (cases) => {
      if (typeof cases === "object" && !Array.isArray(cases)) {
        return Object.entries(cases)
          .map(([key, value]) => {
            const formattedKey = key.replace(/_/g, " "); // Replace underscores with spaces

            if (key.toLowerCase() === "test_steps" && Array.isArray(value)) {
              // If test_steps is an array, format each step on a new line
              const formattedSteps = value
                .map((step, index) => `${step}`) // Number each step
                .join("\n"); // Join with newline
              return `${formattedKey}:\n${formattedSteps}`; // Return formatted steps with line breaks
            }

            // Handle nested objects (e.g., { Expected, Actual })
            if (typeof value === "object" && !Array.isArray(value)) {
              return `${formattedKey}:\n${Object.entries(value)
                .map(([subKey, subValue]) => `  - ${subKey}: ${subValue}`)
                .join("\n")}`;
            }

            // For other fields, just format normally
            return `${formattedKey}: ${value}`;
          })
          .join("\n");
      }
      return cases;
    };

    // Prepare the display value
    const displayValue =
      typeof fieldValue === "object" && !Array.isArray(fieldValue)
        ? formatIdealTestCases(fieldValue) // Handle "Ideal Test Cases"
        : Array.isArray(fieldValue)
        ? fieldValue.join("\n") // Convert arrays to newline-separated string
        : fieldValue || "";

    return (
      <Box
        sx={{ position: "relative", "&:hover .edit-icon": { display: "flex" } }}
      >
        {editMode[field] ? (
          <>
            <TextField
              sx={{
                width: "450px", // Custom width for editing
                transition: "width 0.3s ease",
              }}
              variant="outlined"
              multiline // Always enable multiline
              rows={4} // Default number of rows, adjust as needed
              value={displayValue}
              onChange={(e) => {
                handleFieldChange(field, e.target.value);
              }}
              autoFocus
            />
            <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}>
              <Button
                onClick={() => handleSaveClick(field)}
                size="small"
                sx={{ textTransform: "none" }}
              >
                Save
              </Button>
              <Button
                onClick={() => handleCancelClick(field)}
                size="small"
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
            </Box>
          </>
        ) : (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-line", // Preserve line breaks for strings
              display: "block",
            }}
          >
            {displayValue}
          </Typography>
        )}
      </Box>
    );
  };

  const removeUnderscores = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(removeUnderscores);
    } else if (typeof obj === "object" && obj !== null) {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [
          key.replace(/_/g, " "),
          removeUnderscores(value),
        ])
      );
    }
    return obj;
  };

  return (
    <Box
      className="bug-report-container"
      sx={{
        bgcolor: "#F6F3F2",
        height: "100%",
        p: 2,
        // width: "600px",
        pb: 5,
      }}
    >
      {/* <Container maxWidth="lg"> */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          // maxWidth: 600,
          margin: "0 auto",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          mb={2}
        >
          <Box sx={{ display: "flex" }}>
            <AutomateIcon sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Generate detailed and insightful bug reports with ease
            </Typography>
          </Box>

          {/* <FullscreenToggle /> */}
        </Box>

        {/* //Common Accordion for Input Bug Details */}
        <CommonAccordion
          summary={"Input Bug Details"}
          details={
            <Box
              sx={{
                height: "400px",
                overflowY: "auto",
                paddingRight: "18px",

                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "thin",
                scrollbarColor: "#888 transparent",

                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#555",
                },
              }}
            >
              <Grid container spacing={2} mb={2}>
                {typeof bugReport === "object" ? (
                  <>
                    {bugReport.title && (
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{
                            fontSize: "14px",
                            whiteSpace: "pre-line",
                            fontWeight: "bold",
                            color: "#000",
                          }}
                        >
                          Title
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {bugReport.title}
                        </Typography>
                      </Grid>
                    )}

                    {bugReport.description && (
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{
                            fontSize: "14px",
                            whiteSpace: "pre-line",
                            fontWeight: "bold",
                            color: "#000",
                          }}
                        >
                          Description
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {bugReport.description}
                        </Typography>
                      </Grid>
                    )}

                    {bugReport.environmentDetails && (
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{
                            fontSize: "14px",
                            whiteSpace: "pre-line",
                            fontWeight: "bold",
                            color: "#000",
                          }}
                        >
                          Environment Details
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {bugReport.environmentDetails}
                        </Typography>
                      </Grid>
                    )}

                    {bugReport.stepsToReproduce && (
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{
                            fontSize: "14px",
                            whiteSpace: "pre-line",
                            fontWeight: "bold",
                            color: "#000",
                          }}
                        >
                          Steps to Reproduce
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {bugReport.stepsToReproduce}
                        </Typography>
                      </Grid>
                    )}

                    {bugReport.expectedResult && (
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{
                            fontSize: "14px",
                            whiteSpace: "pre-line",
                            fontWeight: "bold",
                            color: "#000",
                          }}
                        >
                          Expected Result
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {bugReport.expectedResult}
                        </Typography>
                      </Grid>
                    )}

                    {bugReport.actualResult && (
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{
                            fontSize: "14px",
                            whiteSpace: "pre-line",
                            fontWeight: "bold",
                            color: "#000",
                          }}
                        >
                          Actual Result
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {bugReport.actualResult}
                        </Typography>
                      </Grid>
                    )}

                    {bugReport.additionalInfo && (
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{
                            fontSize: "14px",
                            whiteSpace: "pre-line",
                            fontWeight: "bold",
                            color: "#000",
                          }}
                        >
                          Additional Information
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {bugReport.additionalInfo}
                        </Typography>
                      </Grid>
                    )}

                    {bugReport.impact && (
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{
                            fontSize: "14px",
                            whiteSpace: "pre-line",
                            fontWeight: "bold",
                            color: "#000",
                          }}
                        >
                          Impact
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {bugReport.impact}
                        </Typography>
                      </Grid>
                    )}
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Typography
                      variant="body2"
                      mb={2}
                      mt={2}
                      style={{ whiteSpace: "pre-line" }}
                    >
                      {bugReport}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          }
          type={"output"}
          fullScreenMode={true}
        />

        {/* // Enhance Bug Report Details Output */}
        <CommonAccordion
          defaultExpanded={true}
          summary={"Enhance Bug Description"}
          details={
            <Box
              sx={{
                height: "400px",
                overflowY: "auto",
                paddingRight: "18px",

                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "thin",
                scrollbarColor: "#888 transparent",

                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#555",
                },
              }}
            >
              <Box sx={{ px: 1 }}>
                {" "}
                <Grid container spacing={2}>
                  {renderFieldsFromResponse(enhancedBugReport)}
                </Grid>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", margin: 2 }}>
                {/* Thumbs Up Button with Tooltip */}
                <Tooltip title="Like" arrow>
                  <Box
                    onClick={() => handleThumbClick("like")}
                    sx={{
                      cursor: "pointer",
                      marginRight: 2, // Margin between the buttons
                      "&:hover": {
                        color: "#49A3FF", // Change color on hover
                      },
                    }}
                  >
                    {activeThumb === "like" ? (
                      <ThumbUpIcon sx={{ fontSize: "24px" }} /> // Filled thumbs up when clicked
                    ) : (
                      <ThumbUpOutlinedIcon sx={{ fontSize: "24px" }} /> // Outlined thumbs up by default
                    )}
                  </Box>
                </Tooltip>

                {/* Thumbs Down Button with Tooltip */}
                <Tooltip title="Dislike" arrow>
                  <Box
                    onClick={() => handleThumbClick("dislike")}
                    sx={{
                      cursor: "pointer",
                      marginRight: 2, // Margin between the buttons
                      "&:hover": {
                        color: "#FF4842", // Change color on hover
                      },
                    }}
                  >
                    {activeThumb === "dislike" ? (
                      <ThumbDownIcon sx={{ fontSize: "24px" }} /> // Filled thumbs down when clicked
                    ) : (
                      <ThumbDownOutlinedIcon sx={{ fontSize: "24px" }} /> // Outlined thumbs down by default
                    )}
                  </Box>
                </Tooltip>

                <CommonCopy contentToCopy={handleCopyClickData()} />
              </Box>
            </Box>
          }
          type={"output"}
          fullScreenMode={true}
          buttons={[
            // {
            //   label: "Exports",
            //   onClick: handleDownload,
            //   variant: "text",
            //   startIcon: (
            //     <ExportIcon
            //       style={{ background: "none", border: "none", opacity: 0.5 }}
            //     />
            //   ),
            //   disabled: true,
            // },

            {
              label: "Download",
              startIcon: <FileDownloadOutlinedIcon />,
              variant: "contained",
              sx: { float: "right" },
              onClick: () => setOpenDownload(true),
            },
          ]}
          sx={{
            "&:hover, &:focus": {
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              borderRadius: "16px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
            },
          }}
        />
        <CommonDownload
          open={openDownload}
          onClose={() => setOpenDownload(false)}
          serviceName="Bug Report"
          wordData={[
            {
              heading: "Enhanced Bug Reports",
              type: "subParagraphs",
              data: FormatDataForPDF(removeUnderscores(fields)),
            },
          ]}
          serviceResponse={[
            {
              heading: "Enhanced Bug Report",
              type: "subParagraphs",
              data: FormatDataForPDF(removeUnderscores(fields)),
            },
          ]}
          documents={["docx", "pdf"]}
        />
      </Box>
      {/* </Container> */}
    </Box>
  );
};

export default BugReportEnhancerPage;
